/**
 * Copyright (c) Mik BRY
 * mik@mikbry.com
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 */
import { friendlyDate } from '@ziiircom/common';
import { useSelector } from "@ziiircom/messenger/src/utils/store";
import Interface from '../../interface';
import Carousel from '../Carousel';
import Theme from '../../Theme';
import FileInput from "../FileInput";
import Avatar from "../Avatar";
import EmptyAvatar from "../EmptyAvatar";

const buttonBorder = (palette, border = 'none') =>
    palette.button && palette.button.border ? palette.button.border : border;

const buttonBackgroundColor = (palette) =>
    palette.button && palette.button.background ? palette.button.background : palette.surface;
const buttonColor = (palette) => (palette.button && palette.button.color ? palette.button.color : palette.onSurface);
const Styled = Interface.styled('div')`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin: ${(props) => (props.hasPrevious ? '0px' : '12px')} 0px 0px;

  cursor: default;

  margin-left: ${(props) => (props.fromUser ? 'auto' : 0)};

  &:first-child {
    margin-top: 0px;
  }
  
  &.ziiir-message-temporary {
    display: none;
  }
  &.ziiir-message-temporary:last-child {
    display: flex;
  }

  & > div {
    display: flex;
    flex-direction: column;
    align-items: ${(props) => (props.fromUser ? 'flex-end' : 'flex-start')};
    animation: 0.3s cubic-bezier(0, 0, 0.2, 1) 0s 1 normal both running ${(props) =>
    props.fromUser ? 'enterMessageFromUser' : 'enterMessageFromBot'};
    width: 96%;
  }
  & > div > div.ziiir-message-content {
    background-color: ${(props) => (props.fromUser ? props.theme.palette.surface : props.theme.palette.secondary)};
    color: ${(props) => (props.fromUser ? props.theme.palette.onSurface : props.theme.palette.onSecondary)};
    box-shadow: ${(props) => props.theme.message.shadow};
    margin-bottom: 4px;
    padding: 6px 16px;
    border-radius: ${(props) => {
  let { smallRadius, radius } = props.theme.message;
  smallRadius += 'px';
  radius += 'px';
  return props.fromUser
      ? `${radius} ${props.hasPrevious ? smallRadius : radius} ${props.hasNext ? smallRadius : `${radius} ${radius}`}`
      : `${props.hasPrevious ? smallRadius : radius} ${radius} ${radius} ${props.hasNext ? smallRadius : radius}`;
}};
    overflow: auto;
    overflow-wrap: break-word;
    line-height: 1.44;
    width: fit-content;
    max-width: 82%;
  }
  & > div > span {
    text-align: ${(props) => (props.fromUser ? 'right' : 'left')};
    display: block;
    margin: 0px 16px;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) 0s;
    opacity: 0.5;
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1.66;
  }
  & > div > div.ziiir-message-content > a {
    color: ${(props) => props.theme.palette.link || 'inherit'};
  }
  & > div > div.ziiir-message-content > a > img {
    max-width: 100%;
    display: initial;
  }
  & > div > div.ziiir-message-content > img {
    max-width: 100%;
    display: initial;
  }
  & > div button {
    border: ${(props) => buttonBorder(props.theme.palette)};
    border-radius: 4px;
    min-height: 24px;
    margin: 8px 4px;
    background: ${(props) => buttonBackgroundColor(props.theme.palette)};
    color: ${(props) => buttonColor(props.theme.palette)};
    cursor: pointer;
    font-size: 16px;
    font-family: ${(props) => props.theme.font.family};
    font-weight: ${(props) => props.theme.font.weight};
    padding: 1px 6px;
  }
  & > div button:focus {
    outline: none !important;
  }
  @keyframes enterMessageFromUser {
    0% {
      transform: translate( 0, 20px );
      opacity: 0;
    }
  
    100% {
      transform: translate( 0, 0 );
      opacity: 1;
    }
  }

  @keyframes enterMessageFromBot {
    0% {
      transform: translate( - $message-avatar-size, 0 );
      opacity: 0;
    }
  
    100% {
      transform: translate( 0, 0 );
      opacity: 1;
    }
  }
`;

const Template = Interface.styled('div')`
  background: none;
  & button {
    border: ${(props) => buttonBorder(props.theme.palette)};
    border-radius: 4px;
    min-height: 24px;
    margin: 8px 4px;
    background: ${(props) => buttonBackgroundColor(props.theme.palette)};
    color: ${(props) => buttonColor(props.theme.palette)};
    cursor: pointer;
    font-size: 16px;
    font-family: ${(props) => props.theme.font.family};
    font-weight: ${(props) => props.theme.font.weight};
    padding: 1px 6px;
  }
  & button:focus {
    outline: none !important;
  }
`;

Styled.defaultProps = {
  theme: Theme,
};

const StyledReplies = Interface.styled('div')`
  display: flex;
  flex-flow: row wrap;
  width: 100%;
  & > button {
    border: ${(props) => buttonBorder(props.theme.palette, `1px solid ${props.theme.palette.secondary}`)};
    border-radius: 4px;
    min-height: 24px;
    margin: 8px 4px;
    background: ${(props) => buttonBackgroundColor(props.theme.palette)};
    color: ${(props) => buttonColor(props.theme.palette)};
    cursor: pointer;
    font-size: 16px;
  }
`;

StyledReplies.defaultProps = {
  theme: Theme,
};

const e = Interface.createElement;
const Message = ({
  createdtime,
  avatar,
  fromUser = true,
  children,
  onAction = () => {},
  hideDate = false,
  hasPrevious = false,
  hasNext = false,
  quickReplies,
  template,
  showTemplateActions = true
}) => {
  const [messenger] = useSelector((state) => [
    state.messenger
  ]);

  const meta = friendlyDate(createdtime);
  const htmlParts = Array.isArray(children) ? children : [children];

  const getBodyExtraConfig = (messageText) => {
    const extraConfig = {};
    return [messageText?.replace(/{@(([a-zA-Z0-9_-]+)(=([a-zA-Z0-9 _-]+))?)@}/g, (match, _fullEq, optName, _eq, optValue) => {
      extraConfig[optName] = undefined === optValue ? true : optValue;
      return '';
    }), extraConfig]
  };

  let extraConfig = {};
  let body = children.map((_child) => {
    let child = _child;
    [child, extraConfig] = getBodyExtraConfig(typeof child === 'object' && Object.hasOwn(child, 'title') ? child.title : child);
    return fromUser ? child.replace(/(<([^>]+)>)/gi, "") : child;
  });
  if (Object.hasOwn(extraConfig, 'autoSave') && messenger.dialogOptions.autoSave) {
    messenger.dialogOptions.autoSave.enabled = extraConfig.autoSave !== 'false';
  }

  let computedHtml = '';
  if (Array.isArray(body) && body.filter((m) => m?.trim()).length === 0) {
    return '';
  }

  htmlParts.forEach((pHtmlPart) => {
    let htmlPart = typeof pHtmlPart === 'object' && Object.hasOwn(pHtmlPart, 'title') ? pHtmlPart.title : pHtmlPart;
    if (
        !fromUser &&
        typeof htmlPart === 'string' &&
        (htmlPart.indexOf('/>') > 0 || htmlPart.indexOf('</') > 0 || htmlPart.indexOf('/&gt;') > 0 || htmlPart.indexOf('&lt;/') > 0)
    ) {
      [htmlPart, extraConfig] = getBodyExtraConfig(htmlPart);
      computedHtml += htmlPart;
      body = undefined;
    } else if (
        typeof htmlPart === 'string' && htmlPart.match(/(\.(jpg|jpeg|gif|png|svg)$)|(^data:image)/gi)
    ) {
      computedHtml += `<img src="${htmlPart}" alt="" />`
      body = undefined;
    }
  });

  const html = { __html: computedHtml };

  const findNodeByTagName = (_node, tagName, limit = 3) => {
    let node = _node;
    let i = 0;
    while (node?.tagName !== tagName && i < limit) {
      node = node.parentNode;
      i += 1;
    }

    return i < limit ? node : null;
  }

  const handleClick = (event) => {
    const nodeClicked = findNodeByTagName(event.target, 'BUTTON');
    if (nodeClicked) {
      onAction(nodeClicked.tagName, {
        value: nodeClicked.value ?? nodeClicked.textContent,
        title: nodeClicked.innerHTML
      });
    }
  };

  const handleQuickClick = (event) => {
    event.preventDefault();
    let parent = event.target.parentNode;
    let i = 0;
    while (!parent?.classList?.contains('ziiir-quick-replies') && i < 3) {
      parent = parent.parentNode;
      i += 1;
    }
    // parent.style.display = 'none';
    parent.parentNode.removeChild(parent);
  };

  const handleCarouselAction = (event, payload) => {
    event.stopPropagation();
    event.preventDefault();
    onAction('BUTTON', payload);
    const parent = event.target.parentNode;
    // parent.parentNode.style.display = 'none';
    parent.parentNode.parentNode.removeChild(parent.parentNode);
  };

  let lastComponent;
  if (quickReplies) {
    const computedQuickReplies = [];

    quickReplies.forEach((qr) => {
      const options = {
        key: qr.title,
        value: Object.hasOwn(qr, 'value') ? qr.value : qr.title,
        className: `ziiir-quickreply${Object.hasOwn(qr, 'className') ? ` ${qr.className}` : ''}`,
        onClick: handleQuickClick,
        dangerouslySetInnerHTML: { __html: qr.title }
      }
      if (qr.disabled) {
        options.disabled = 'disabled';
      }
      if (qr.prefix) {
        computedQuickReplies.push(e('div', { className: 'ziiir-quickreply-prefix', dangerouslySetInnerHTML: { __html: qr.prefix } }));
      }
      computedQuickReplies.push(e('button', options));
      if (qr.suffix) {
        computedQuickReplies.push(e('div', { className: 'ziiir-quickreply-suffix', dangerouslySetInnerHTML: { __html: qr.suffix } }));
      }
    });
    lastComponent = e(
      StyledReplies,
      { className: 'ziiir-quick-replies' },
      computedQuickReplies,
    );
  } /* else if (template) {
    lastComponent = e(Carousel, { ...template, onAction: handleCarouselAction });
  } */
  else if (!hideDate && !hasNext) {
    lastComponent = e('span', null, meta);
  }
  const extraClassNames = `${Object.hasOwn(extraConfig, 'className') ? (` ${  extraConfig.className}`) : ''}${Object.hasOwn(extraConfig, 'temporary') && extraConfig.temporary ? ' ziiir-message-temporary' : ''}`;
  const messageContainer = e(
    Styled,
    { className: `ziiir-message-container${extraClassNames}`, fromUser, 'created-time': createdtime, hasPrevious, hasNext },
    avatar && (hasPrevious ? e(EmptyAvatar) : e(Avatar, { className: 'ziiir-message-avatar', src: avatar.src, alt: avatar.name })),
    e('div', { className: 'ziiir-message', onClick: handleClick }, e('div', { className: 'ziiir-message-content', dangerouslySetInnerHTML: html }, body), lastComponent),
  );
  if (template) {
    switch (template.type) {
      case 'file': {
        return e(Template, { className: `ziiir-file-input${extraClassNames}` }, messageContainer, e(FileInput, {
          options: template.options ? template.options : {},
          onAction,
          showTemplateActions
        }));
      }
      case 'carousel':
        return e(Template, { className: `ziiir-carousel${extraClassNames}` }, messageContainer, e(Carousel, {...template, onAction: handleCarouselAction, showTemplateActions}));
      default:
        return e('div', { className: `ziiir-message${extraClassNames}` }, e('div', {
          className: 'ziiir-message-content',
          dangerouslySetInnerHTML: html
        }, body), lastComponent);
    }
  }
  return messageContainer;
};

export default Message;
